/**
 * The last line of defence against a render-time throw.
 *
 * Without a boundary any uncaught error while rendering blanks the entire SPA.
 * With one, the broken subtree is swapped for a fallback that (a) keeps the
 * shell and every other route alive, (b) offers a retry that re-mounts just the
 * failed subtree before resorting to a full reload, and (c) hands the user a
 * short error reference plus a one-click copy of the client log — which is the
 * difference between "it broke" and a reproducible bug report.
 *
 * Error boundaries must be class components: there is no hook equivalent for
 * `getDerivedStateFromError` / `componentDidCatch`.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { formatLogs, logStore, logger } from '@/lib/logger';
import { Button } from './ui';
import { Icon } from './icons';

const log = logger.child('boundary');

interface Props {
  children: ReactNode;
  /** Names the failed area in the fallback copy, e.g. "This page". */
  label?: string;
  /** Called after a successful retry — e.g. to reset a query error state. */
  onReset?: () => void;
}
interface State {
  error: Error | null;
  /** Short, human-quotable reference for this crash. */
  ref: string | null;
  copied: boolean;
}

/** Six characters is enough to disambiguate within one support conversation. */
function makeRef(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, ref: null, copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, ref: makeRef() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack stays in the console for a developer; the log buffer carries
    // the message and location so the copied transcript is self-contained.
    console.error('Uncaught render error:', error, info.componentStack);
    log.error(`Render crash in ${this.props.label ?? 'the app'}`, {
      name: error.name,
      detail: error.message,
      ref: this.state.ref ?? undefined,
      componentStack: info.componentStack?.split('\n').slice(0, 6).join('\n'),
    });
  }

  private retry = (): void => {
    log.info('Retrying after render crash', { ref: this.state.ref ?? undefined });
    this.setState({ error: null, ref: null, copied: false });
    this.props.onReset?.();
  };

  private copyDiagnostics = async (): Promise<void> => {
    const { error, ref } = this.state;
    const report = [
      `ALFIE error report ${ref ?? ''}`.trim(),
      `When: ${new Date().toISOString()}`,
      `Where: ${window.location.pathname}`,
      `Error: ${error?.name}: ${error?.message}`,
      '',
      '--- recent client log ---',
      formatLogs(logStore.snapshot()),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(report);
      this.setState({ copied: true });
    } catch {
      // Clipboard unavailable (insecure origin / denied). Fall back to the
      // console so the report is still recoverable.
      console.info(report);
      this.setState({ copied: true });
    }
  };

  override render(): ReactNode {
    const { error, ref, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="empty danger" role="alert" style={{ padding: 'var(--s7) var(--s5)' }}>
        <Icon.Alert className="glyph" aria-hidden />
        <div className="stack gap-2 items-center">
          <span className="empty-title">Something went wrong</span>
          <span className="empty-hint">
            {this.props.label ?? 'This part of the app'} stopped unexpectedly. Nothing you had saved is affected —
            your work is stored on the server.
          </span>
          {error.message && <span className="t-xs mono muted-more measure">{error.message}</span>}
          {ref && (
            <span className="t-xs muted-more">
              Reference <strong className="mono">{ref}</strong>
            </span>
          )}
        </div>
        <div className="row gap-2 wrap justify-center">
          <Button variant="secondary" onClick={this.retry}>
            <Icon.Refresh aria-hidden /> Try again
          </Button>
          <Button variant="ghost" onClick={() => window.location.reload()}>
            Reload the app
          </Button>
          <Button variant="ghost" onClick={() => void this.copyDiagnostics()}>
            {copied ? <Icon.Check aria-hidden /> : <Icon.Copy aria-hidden />}
            {copied ? 'Copied' : 'Copy diagnostics'}
          </Button>
        </div>
      </div>
    );
  }
}
